import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  FaPlus,
  FaTrash,
  FaSave,
  FaShare,
  FaShoppingBag,
  FaRedo,
  FaUndo,
  FaTshirt,
  FaPalette,
} from 'react-icons/fa';
import LoadingSpinner from '../components/LoadingSpinner';
import ItemModal from '../components/ItemModal';
import '../styles/CustomizeOutfit.css';

const CustomizeOutfit = ({ onAddToCart }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tops, setTops] = useState([]);
  const [bottoms, setBottoms] = useState([]);
  const [shoes, setShoes] = useState([]);
  const [accessories, setAccessories] = useState([]);

  const [selectedItems, setSelectedItems] = useState({
    top: null,
    bottom: null,
    shoes: null,
    accessories: [],
  });

  const [showItemModal, setShowItemModal] = useState(false);
  const [currentCategory, setCurrentCategory] = useState('');
  const [outfitName, setOutfitName] = useState('My Custom Outfit');
  const [savedOutfits, setSavedOutfits] = useState([]);
  const [activeTab, setActiveTab] = useState('items');
  const [colorScheme, setColorScheme] = useState('neutral');
  const [canvasHistory, setCanvasHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isLoggedIn] = useState(!!localStorage.getItem('token'));

  const canvasRef = useRef(null);

  // Memoized color schemes for better performance
  const colorSchemes = useMemo(
    () => ({
      neutral: {
        name: 'Neutral',
        description: 'A timeless palette with balanced tones that work well with any style.',
        colors: ['#f5f5f5', '#e0e0e0', '#9e9e9e', '#616161', '#212121'],
      },
      warm: {
        name: 'Warm Tones',
        description: 'Rich, inviting colors that create a cozy and energetic look.',
        colors: ['#ffebee', '#ffcdd2', '#ef9a9a', '#e57373', '#ef5350'],
      },
      cool: {
        name: 'Cool Tones',
        description: 'Refreshing blues and cool tones for a calm, sophisticated appearance.',
        colors: ['#e3f2fd', '#bbdefb', '#90caf9', '#64b5f6', '#42a5f5'],
      },
      monochrome: {
        name: 'Monochrome',
        description: 'Elegant simplicity with variations of a single color.',
        colors: ['#fafafa', '#f5f5f5', '#eeeeee', '#e0e0e0', '#bdbdbd'],
      },
      contrast: {
        name: 'High Contrast',
        description: 'Bold combinations that make a strong visual statement.',
        colors: ['#ffffff', '#f44336', '#2196f3', '#ffeb3b', '#000000'],
      },
    }),
    [],
  );

  // Optimized product fetching with error handling
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/shop/products?per_page=100');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const products = data.products || [];

      // Categorize products efficiently
      setTops(
        products.filter(
          product =>
            product.category === 'Tops' ||
            product.category === 'Shirts' ||
            product.category === 'Blouses',
        ),
      );

      setBottoms(
        products.filter(
          product =>
            product.category === 'Bottoms' ||
            product.category === 'Pants' ||
            product.category === 'Skirts' ||
            product.category === 'Jeans',
        ),
      );

      setShoes(
        products.filter(
          product =>
            product.category === 'Shoes' ||
            product.category === 'Sneakers' ||
            product.category === 'Boots',
        ),
      );

      setAccessories(
        products.filter(
          product =>
            product.category === 'Accessories' ||
            product.category === 'Jewelry' ||
            product.category === 'Bags' ||
            product.category === 'Hats' ||
            product.category === 'Belts',
        ),
      );
    } catch (err) {
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch saved outfits
  const fetchSavedOutfits = useCallback(async () => {
    if (!isLoggedIn) {
      return;
    }

    try {
      const response = await fetch('/api/custom-outfits', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSavedOutfits(data);
      }
    } catch (err) {
      // Error will be handled by global error handler
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchProducts();
    fetchSavedOutfits();
  }, [fetchProducts, fetchSavedOutfits]);

  const openItemModal = category => {
    setCurrentCategory(category);
    setShowItemModal(true);
  };

  const handleSelectItem = item => {
    // Save current state to history for undo/redo
    const newHistory = canvasHistory.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(selectedItems)));
    setCanvasHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);

    // Update selected items
    if (currentCategory === 'accessories') {
      if (!selectedItems.accessories.some(acc => acc.id === item.id)) {
        setSelectedItems(prev => ({
          ...prev,
          accessories: [...prev.accessories, item],
        }));
      }
    } else {
      setSelectedItems(prev => ({
        ...prev,
        [currentCategory]: item,
      }));
    }

    setShowItemModal(false);
  };

  const handleRemoveItem = useCallback(
    (category, itemId) => {
      // Save current state to history for undo/redo
      const newHistory = canvasHistory.slice(0, historyIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(selectedItems)));
      setCanvasHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);

      if (category === 'accessories') {
        setSelectedItems(prev => ({
          ...prev,
          accessories: prev.accessories.filter(item => item.id !== itemId),
        }));
      } else {
        setSelectedItems(prev => ({
          ...prev,
          [category]: null,
        }));
      }
    },
    [canvasHistory, historyIndex, selectedItems],
  );

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setSelectedItems(JSON.parse(JSON.stringify(canvasHistory[historyIndex - 1])));
    }
  };

  const handleRedo = () => {
    if (historyIndex < canvasHistory.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setSelectedItems(JSON.parse(JSON.stringify(canvasHistory[historyIndex + 1])));
    }
  };

  const handleSaveOutfit = async () => {
    if (!isLoggedIn) {
      window.location.href = '/auth?returnTo=/customize-outfit';
      return;
    }

    try {
      const outfitData = {
        name: outfitName,
        items: {
          top: selectedItems.top ? selectedItems.top.id : null,
          bottom: selectedItems.bottom ? selectedItems.bottom.id : null,
          shoes: selectedItems.shoes ? selectedItems.shoes.id : null,
          accessories: selectedItems.accessories.map(item => item.id),
        },
      };

      const response = await fetch('/api/custom-outfits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(outfitData),
      });

      if (response.ok) {
        const savedOutfit = await response.json();
        setSavedOutfits([...savedOutfits, savedOutfit]);
        alert('Outfit saved successfully!');
      } else {
        throw new Error('Failed to save outfit');
      }
    } catch (err) {
      alert('Failed to save outfit. Please try again.');
    }
  };

  const handleAddOutfitToCart = () => {
    const allItems = [];

    if (selectedItems.top) {
      allItems.push(selectedItems.top);
    }
    if (selectedItems.bottom) {
      allItems.push(selectedItems.bottom);
    }
    if (selectedItems.shoes) {
      allItems.push(selectedItems.shoes);
    }
    allItems.push(...selectedItems.accessories);

    allItems.forEach(item => {
      onAddToCart(item, 1);
    });

    alert('All outfit items added to your cart!');
  };

  const calculateTotalPrice = () => {
    let total = 0;

    if (selectedItems.top) {
      total += selectedItems.top.price || 0;
    }
    if (selectedItems.bottom) {
      total += selectedItems.bottom.price || 0;
    }
    if (selectedItems.shoes) {
      total += selectedItems.shoes.price || 0;
    }

    selectedItems.accessories.forEach(item => {
      total += item.price || 0;
    });

    return total.toFixed(2);
  };

  const handleShareOutfit = () => {
    const url = `${window.location.origin}/customize-outfit?share=${btoa(
      JSON.stringify({
        top: selectedItems.top ? selectedItems.top.id : null,
        bottom: selectedItems.bottom ? selectedItems.bottom.id : null,
        shoes: selectedItems.shoes ? selectedItems.shoes.id : null,
        accessories: selectedItems.accessories.map(item => item.id),
      }),
    )}`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        alert('Outfit link copied to clipboard!');
      })
      .catch(() => {
        alert('Failed to copy link. Please try again.');
      });
  };

  const applyColorScheme = scheme => {
    setColorScheme(scheme);
  };

  // Memoized outfit canvas for better performance
  const outfitCanvas = useMemo(() => {
    if (loading) {
      return (
        <div className='canvas-loading'>
          <LoadingSpinner />
          <p>Loading products...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className='canvas-error'>
          <h3>Oops! Something went wrong</h3>
          <p>{error}</p>
          <button onClick={fetchProducts} className='retry-btn'>
            Try Again
          </button>
        </div>
      );
    }

    return (
      <div className={`outfit-canvas ${colorScheme}`}>
        {/* Top item */}
        <div className='canvas-item top-item'>
          {selectedItems.top ? (
            <div className='selected-item'>
              <img
                src={selectedItems.top.image_url || '/assets/placeholder.jpg'}
                alt={selectedItems.top.name}
                loading='lazy'
                onError={e => {
                  e.target.src = '/assets/placeholder.jpg';
                }}
              />
              <button
                className='remove-item-btn'
                onClick={() => handleRemoveItem('top', selectedItems.top.id)}
              >
                <FaTrash />
              </button>
            </div>
          ) : (
            <button className='add-item-btn' onClick={() => openItemModal('top')}>
              <FaPlus /> Add Top
            </button>
          )}
        </div>

        {/* Bottom item */}
        <div className='canvas-item bottom-item'>
          {selectedItems.bottom ? (
            <div className='selected-item'>
              <img
                src={selectedItems.bottom.image_url || '/assets/placeholder.jpg'}
                alt={selectedItems.bottom.name}
                loading='lazy'
                onError={e => {
                  e.target.src = '/assets/placeholder.jpg';
                }}
              />
              <button
                className='remove-item-btn'
                onClick={() => handleRemoveItem('bottom', selectedItems.bottom.id)}
              >
                <FaTrash />
              </button>
            </div>
          ) : (
            <button className='add-item-btn' onClick={() => openItemModal('bottom')}>
              <FaPlus /> Add Bottom
            </button>
          )}
        </div>

        {/* Shoes item */}
        <div className='canvas-item shoes-item'>
          {selectedItems.shoes ? (
            <div className='selected-item'>
              <img
                src={selectedItems.shoes.image_url || '/assets/placeholder.jpg'}
                alt={selectedItems.shoes.name}
                loading='lazy'
                onError={e => {
                  e.target.src = '/assets/placeholder.jpg';
                }}
              />
              <button
                className='remove-item-btn'
                onClick={() => handleRemoveItem('shoes', selectedItems.shoes.id)}
              >
                <FaTrash />
              </button>
            </div>
          ) : (
            <button className='add-item-btn' onClick={() => openItemModal('shoes')}>
              <FaPlus /> Add Shoes
            </button>
          )}
        </div>

        {/* Accessories */}
        <div className='canvas-item accessories-container'>
          {selectedItems.accessories.map(item => (
            <div key={item.id} className='accessory-item'>
              <img
                src={item.image_url || '/assets/placeholder.jpg'}
                alt={item.name}
                loading='lazy'
                onError={e => {
                  e.target.src = '/assets/placeholder.jpg';
                }}
              />
              <button
                className='remove-item-btn'
                onClick={() => handleRemoveItem('accessories', item.id)}
              >
                <FaTrash />
              </button>
            </div>
          ))}

          {selectedItems.accessories.length < 3 && (
            <button
              className='add-item-btn accessory-add-btn'
              onClick={() => openItemModal('accessories')}
            >
              <FaPlus /> Add Accessory
            </button>
          )}
        </div>
      </div>
    );
  }, [loading, error, selectedItems, colorScheme, fetchProducts, handleRemoveItem]);

  return (
    <div className={`customize-outfit-page color-scheme-${colorScheme}`}>
      {/* Hero Section */}
      <div className='customize-hero'>
        <div className='hero-background'></div>
        <div className='hero-overlay'></div>
        <div className='customize-container'>
          <div className='hero-content'>
            <div className='hero-text-container'>
              <h1 className='hero-title'>
                <span className='main-title'>Style Studio</span>
                <span className='sub-title'>Your Personal Fashion Designer</span>
              </h1>
              <p className='hero-description'>
                Create stunning outfits with our interactive styling tool. Mix, match, and perfect
                your look with our curated collection. Design your signature style and bring your
                fashion vision to life.
              </p>
              <div className='hero-features'>
                <div className='feature-item'>
                  <span className='feature-icon'>✨</span>
                  <span>AI-Powered Styling</span>
                </div>
                <div className='feature-item'>
                  <span className='feature-icon'>🎨</span>
                  <span>Color Coordination</span>
                </div>
                <div className='feature-item'>
                  <span className='feature-icon'>💾</span>
                  <span>Save & Share</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='customize-container'>
        <div className='customize-content'>
          {/* Canvas section */}
          <div className='canvas-section'>
            <div className='canvas-header'>
              <div className='canvas-title-section'>
                <h2>Your Outfit Canvas</h2>
                <input
                  type='text'
                  value={outfitName}
                  onChange={e => setOutfitName(e.target.value)}
                  className='outfit-name-input'
                  placeholder='Enter outfit name'
                />
              </div>

              <div className='canvas-actions'>
                <button
                  className='action-btn undo-btn'
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                  title='Undo last action'
                >
                  <FaUndo />
                  <span>Undo</span>
                </button>
                <button
                  className='action-btn redo-btn'
                  onClick={handleRedo}
                  disabled={historyIndex >= canvasHistory.length - 1}
                  title='Redo last action'
                >
                  <FaRedo />
                  <span>Redo</span>
                </button>
              </div>
            </div>

            <div className='canvas-container' ref={canvasRef}>
              {outfitCanvas}
            </div>

            <div className='outfit-summary'>
              <div className='summary-header'>
                <h3>Outfit Summary</h3>
                <div className='outfit-total'>
                  <span className='total-label'>Total Price:</span>
                  <span className='total-price'>${calculateTotalPrice()}</span>
                </div>
              </div>

              <div className='outfit-actions'>
                <button
                  className='primary-btn save-btn'
                  onClick={handleSaveOutfit}
                  disabled={
                    !selectedItems.top &&
                    !selectedItems.bottom &&
                    !selectedItems.shoes &&
                    selectedItems.accessories.length === 0
                  }
                >
                  <FaSave />
                  <span>Save Outfit</span>
                </button>

                <button
                  className='secondary-btn share-btn'
                  onClick={handleShareOutfit}
                  disabled={
                    !selectedItems.top &&
                    !selectedItems.bottom &&
                    !selectedItems.shoes &&
                    selectedItems.accessories.length === 0
                  }
                >
                  <FaShare />
                  <span>Share</span>
                </button>

                <button
                  className='accent-btn cart-btn'
                  onClick={handleAddOutfitToCart}
                  disabled={
                    !selectedItems.top &&
                    !selectedItems.bottom &&
                    !selectedItems.shoes &&
                    selectedItems.accessories.length === 0
                  }
                >
                  <FaShoppingBag />
                  <span>Add to Cart</span>
                </button>
              </div>
            </div>
          </div>

          {/* Options and items section */}
          <div className='options-section'>
            <div className='section-header'>
              <h2>Style Your Outfit</h2>
              <p>Choose items and customize your perfect look</p>
            </div>

            <div className='options-tabs'>
              <button
                className={`tab-btn ${activeTab === 'items' ? 'active' : ''}`}
                onClick={() => setActiveTab('items')}
              >
                <FaTshirt />
                <span>Items</span>
              </button>

              <button
                className={`tab-btn ${activeTab === 'color' ? 'active' : ''}`}
                onClick={() => setActiveTab('color')}
              >
                <FaPalette />
                <span>Color Schemes</span>
              </button>

              {isLoggedIn && (
                <button
                  className={`tab-btn ${activeTab === 'saved' ? 'active' : ''}`}
                  onClick={() => setActiveTab('saved')}
                >
                  <FaSave />
                  <span>Saved Outfits</span>
                </button>
              )}
            </div>

            <div className='options-content'>
              {activeTab === 'items' && (
                <div className='items-section'>
                  <div className='items-grid'>
                    <div className='item-category-card'>
                      <div className='category-header'>
                        <FaTshirt className='category-icon' />
                        <h3>Tops</h3>
                        <span className='item-count'>{tops.length} items</span>
                      </div>
                      <p className='category-description'>
                        Choose from shirts, blouses, and jackets
                      </p>
                      <button className='category-btn primary' onClick={() => openItemModal('top')}>
                        Browse Tops
                      </button>
                    </div>

                    <div className='item-category-card'>
                      <div className='category-header'>
                        <FaTshirt className='category-icon' />
                        <h3>Bottoms</h3>
                        <span className='item-count'>{bottoms.length} items</span>
                      </div>
                      <p className='category-description'>Pants, skirts, and shorts collection</p>
                      <button
                        className='category-btn primary'
                        onClick={() => openItemModal('bottom')}
                      >
                        Browse Bottoms
                      </button>
                    </div>

                    <div className='item-category-card'>
                      <div className='category-header'>
                        <FaTshirt className='category-icon' />
                        <h3>Shoes</h3>
                        <span className='item-count'>{shoes.length} items</span>
                      </div>
                      <p className='category-description'>
                        Complete your look with perfect footwear
                      </p>
                      <button
                        className='category-btn primary'
                        onClick={() => openItemModal('shoes')}
                      >
                        Browse Shoes
                      </button>
                    </div>

                    <div className='item-category-card'>
                      <div className='category-header'>
                        <FaTshirt className='category-icon' />
                        <h3>Accessories</h3>
                        <span className='item-count'>{accessories.length} items</span>
                      </div>
                      <p className='category-description'>Add the finishing touches</p>
                      <button
                        className='category-btn primary'
                        onClick={() => openItemModal('accessories')}
                      >
                        Browse Accessories
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'color' && (
                <div className='color-schemes-section'>
                  <div className='schemes-header'>
                    <h3>Choose Your Color Palette</h3>
                    <p>Select a color scheme to enhance your outfit&apos;s visual appeal</p>
                  </div>

                  <div className='scheme-grid'>
                    {Object.entries(colorSchemes).map(([key, scheme]) => (
                      <div
                        key={key}
                        className={`scheme-card ${colorScheme === key ? 'active' : ''}`}
                        onClick={() => applyColorScheme(key)}
                      >
                        <div className='scheme-preview'>
                          <div className='color-palette'>
                            {scheme.colors.map(color => (
                              <div
                                key={`${key}-${color}`}
                                className='color-swatch'
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        </div>
                        <div className='scheme-info'>
                          <h4>{scheme.name}</h4>
                          <p>{scheme.description}</p>
                        </div>
                        {colorScheme === key && (
                          <div className='active-indicator'>
                            <FaPalette />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'saved' && (
                <div className='saved-outfits-section'>
                  {savedOutfits.length === 0 ? (
                    <div className='empty-state'>
                      <div className='empty-icon'>
                        <FaSave />
                      </div>
                      <h3>No Saved Outfits Yet</h3>
                      <p>
                        Create your first outfit and save it to see it here. Your saved outfits will
                        be available across all your devices.
                      </p>
                      <button className='primary-btn' onClick={() => setActiveTab('items')}>
                        Start Creating
                      </button>
                    </div>
                  ) : (
                    <div className='saved-outfits-container'>
                      <div className='saved-header'>
                        <h3>Your Saved Outfits</h3>
                        <p>
                          {savedOutfits.length} outfit{savedOutfits.length !== 1 ? 's' : ''} saved
                        </p>
                      </div>

                      <div className='outfits-grid'>
                        {savedOutfits.map(outfit => (
                          <div key={outfit.id} className='saved-outfit-card'>
                            <div className='outfit-card-header'>
                              <h4>{outfit.name}</h4>
                              <div className='outfit-date'>
                                {new Date(outfit.created_at).toLocaleDateString()}
                              </div>
                            </div>

                            <div className='saved-outfit-preview'>
                              <div className='outfit-thumbnail'>
                                <div className='thumbnail-grid'>
                                  {outfit.items.top && (
                                    <div className='thumbnail-item'>
                                      <img
                                        src={
                                          tops.find(item => item.id === outfit.items.top)
                                            ?.image_url || '/assets/placeholder.jpg'
                                        }
                                        alt='Top'
                                        loading='lazy'
                                        onError={e => {
                                          e.target.src = '/assets/placeholder.jpg';
                                        }}
                                      />
                                      <span className='item-label'>Top</span>
                                    </div>
                                  )}
                                  {outfit.items.bottom && (
                                    <div className='thumbnail-item'>
                                      <img
                                        src={
                                          bottoms.find(item => item.id === outfit.items.bottom)
                                            ?.image_url || '/assets/placeholder.jpg'
                                        }
                                        alt='Bottom'
                                        loading='lazy'
                                        onError={e => {
                                          e.target.src = '/assets/placeholder.jpg';
                                        }}
                                      />
                                      <span className='item-label'>Bottom</span>
                                    </div>
                                  )}
                                  {outfit.items.shoes && (
                                    <div className='thumbnail-item'>
                                      <img
                                        src={
                                          shoes.find(item => item.id === outfit.items.shoes)
                                            ?.image_url || '/assets/placeholder.jpg'
                                        }
                                        alt='Shoes'
                                        loading='lazy'
                                        onError={e => {
                                          e.target.src = '/assets/placeholder.jpg';
                                        }}
                                      />
                                      <span className='item-label'>Shoes</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className='saved-outfit-actions'>
                              <button
                                className='primary-btn load-outfit-btn'
                                onClick={() => {
                                  setSelectedItems({
                                    top: tops.find(item => item.id === outfit.items.top) || null,
                                    bottom:
                                      bottoms.find(item => item.id === outfit.items.bottom) || null,
                                    shoes:
                                      shoes.find(item => item.id === outfit.items.shoes) || null,
                                    accessories: outfit.items.accessories
                                      .map(id => accessories.find(item => item.id === id))
                                      .filter(Boolean),
                                  });
                                  setActiveTab('items');
                                }}
                              >
                                Load Outfit
                              </button>

                              <button
                                className='danger-btn delete-outfit-btn'
                                onClick={async () => {
                                  if (
                                    window.confirm('Are you sure you want to delete this outfit?')
                                  ) {
                                    try {
                                      const response = await fetch(
                                        `/api/custom-outfits/${outfit.id}`,
                                        {
                                          method: 'DELETE',
                                          headers: {
                                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                                          },
                                        },
                                      );

                                      if (response.ok) {
                                        setSavedOutfits(
                                          savedOutfits.filter(o => o.id !== outfit.id),
                                        );
                                      } else {
                                        throw new Error('Failed to delete outfit');
                                      }
                                    } catch (err) {
                                      alert('Failed to delete outfit. Please try again.');
                                    }
                                  }
                                }}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Item selection modal */}
      {showItemModal && (
        <ItemModal
          title={`Select ${
            currentCategory === 'accessories'
              ? 'an Accessory'
              : currentCategory === 'top'
                ? 'a Top'
                : currentCategory === 'bottom'
                  ? 'Bottoms'
                  : 'Shoes'
          }`}
          items={
            currentCategory === 'top'
              ? tops
              : currentCategory === 'bottom'
                ? bottoms
                : currentCategory === 'shoes'
                  ? shoes
                  : accessories
          }
          onSelectItem={handleSelectItem}
          onClose={() => setShowItemModal(false)}
        />
      )}
    </div>
  );
};

export default CustomizeOutfit;
